import React, { useState, useEffect, useCallback } from 'react';
import { Box, Flex, Text, VStack, Icon, Tooltip, Collapse } from '@chakra-ui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@chakra-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';

import { adminNavigation } from '../config/navigation';

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Find section containing active route and expand only that one by default
  const getInitialExpandedSections = useCallback(() => {
    const expanded: Record<string, boolean> = {};

    adminNavigation.forEach((section) => {
      const hasActiveRoute = section.items.some((item) => {
        if (item.path === '/admin' || item.path === '/admin/dashboard') {
          return location.pathname === '/admin' || location.pathname === '/admin/dashboard';
        }
        // Keep User Management section expanded for create/edit user routes
        if (item.path === '/admin/users') {
          return (
            location.pathname === '/admin/users' ||
            location.pathname === '/admin/create-user' ||
            location.pathname === '/admin/edit-user'
          );
        }
        return location.pathname === item.path;
      });

      expanded[section.label] = hasActiveRoute;
    });

    return expanded;
  }, [location.pathname]);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    getInitialExpandedSections
  );

  const toggleSection = (sectionLabel: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionLabel]: !prev[sectionLabel],
    }));
  };

  // Update expanded sections when route changes
  useEffect(() => {
    const newExpandedState = getInitialExpandedSections();
    setExpandedSections(newExpandedState);
  }, [getInitialExpandedSections]);

  const isActiveRoute = (path: string) => {
    if (path === '/admin' || path === '/admin/dashboard') {
      return location.pathname === '/admin' || location.pathname === '/admin/dashboard';
    }

    // Keep "All Users" selected when on create-user or edit-user routes
    if (path === '/admin/users') {
      return (
        location.pathname === '/admin/users' ||
        location.pathname === '/admin/create-user' ||
        location.pathname === '/admin/edit-user'
      );
    }

    return location.pathname === path;
  };

  const handleNavigate = (path: string) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  return (
    <Box
      w={isCollapsed ? '60px' : '200px'}
      h="100%"
      bg="white"
      borderRadius="22px"
      overflow="hidden"
      flexShrink={0}
      transition="width 0.25s ease"
      display="flex"
      flexDirection="column"
    >
      {/* Admin Header */}
      <Tooltip
        label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        placement="right"
        hasArrow
      >
        <Flex
          h="50px"
          bg="#171717"
          align="center"
          justify="center"
          px={isCollapsed ? 0 : 4}
          cursor="pointer"
          onClick={onToggle}
          position="relative"
          _hover={{
            bg: '#252525',
          }}
          _active={{
            bg: '#1a1a1a',
          }}
          transition="all 0.2s ease"
          borderBottom="1px solid"
          borderBottomColor="rgba(255, 255, 255, 0.1)"
        >
          {!isCollapsed && (
            <Text
              color="#F4C400"
              fontSize="17px"
              fontFamily="Avantt semibold, sans-serif"
              position="absolute"
              left="50%"
              transform="translateX(-50%)"
              letterSpacing="0.5px"
            >
              Admin
            </Text>
          )}
          <Flex
            align="center"
            justify="center"
            w="28px"
            h="28px"
            position={isCollapsed ? 'static' : 'absolute'}
            right={isCollapsed ? 'auto' : 4}
          >
            <Icon
              as={isCollapsed ? ChevronRightIcon : ChevronLeftIcon}
              boxSize={5}
              color="#F4C400"
              transition="transform 0.3s ease"
            />
          </Flex>
        </Flex>
      </Tooltip>

      {/* Navigation */}
      <VStack
        spacing={0}
        align="stretch"
        px={isCollapsed ? 1.5 : 4}
        pb={4}
        pt={2}
        flex="1"
        overflowY="auto"
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '-ms-overflow-style': 'none',
          scrollbarWidth: 'none',
        }}
      >
        {adminNavigation.map((section) => (
          <Box key={section.label} mb={3}>
            {!isCollapsed ? (
              <>
                {/* Collapsible Section Header */}
                <Flex
                  px={1}
                  mb={1.5}
                  align="center"
                  justify="space-between"
                  cursor="pointer"
                  onClick={() => toggleSection(section.label)}
                  _hover={{ opacity: 0.7 }}
                  transition="opacity 0.2s"
                >
                  <Text fontSize="13px" fontFamily="Avantt semibold, sans-serif" color="#222222">
                    {section.label}
                  </Text>
                  <Icon
                    as={expandedSections[section.label] ? ChevronUpIcon : ChevronDownIcon}
                    boxSize={4}
                    color="#666666"
                    transition="transform 0.2s"
                  />
                </Flex>

                {/* Collapsible Content */}
                <Collapse in={expandedSections[section.label]} animateOpacity>
                  <VStack spacing={0.5} align="stretch">
                    {section.items.map((item) => {
                      const active = isActiveRoute(item.path);

                      return (
                        <Box key={item.path}>
                          <Flex
                            h="40px"
                            w="100%"
                            px={4}
                            align="center"
                            justify="flex-start"
                            gap={3}
                            bg={active ? '#F2BF0A' : 'transparent'}
                            borderRadius="22px"
                            cursor="pointer"
                            transition="background 0.15s ease"
                            onClick={() => handleNavigate(item.path)}
                            _hover={{
                              bg: active ? '#F2BF0A' : '#F7F7F7',
                            }}
                          >
                            <Icon
                              as={item.icon}
                              boxSize="21px"
                              flexShrink={0}
                              color={active ? '#111111' : '#666666'}
                            />
                            <Text
                              fontSize="13px"
                              lineHeight="1"
                              fontFamily="Avantt semibold, sans-serif"
                              color={active ? '#111111' : '#666666'}
                              whiteSpace="nowrap"
                            >
                              {item.label}
                            </Text>
                          </Flex>
                        </Box>
                      );
                    })}
                  </VStack>
                </Collapse>
              </>
            ) : (
              /* Collapsed sidebar - show icons only with tooltips */
              <VStack spacing={1} align="stretch">
                {section.items.map((item) => {
                  const active = isActiveRoute(item.path);

                  return (
                    <Tooltip key={item.path} label={item.label} placement="right" hasArrow>
                      <Box>
                        <Flex
                          h="36px"
                          w="36px"
                          mx="auto"
                          align="center"
                          justify="center"
                          bg={active ? '#F2BF0A' : 'transparent'}
                          borderRadius="full"
                          cursor="pointer"
                          transition="background 0.15s ease"
                          onClick={() => handleNavigate(item.path)}
                          _hover={{
                            bg: active ? '#F2BF0A' : '#F7F7F7',
                          }}
                        >
                          <Icon
                            as={item.icon}
                            boxSize="19px"
                            flexShrink={0}
                            color={active ? '#111111' : '#666666'}
                          />
                        </Flex>
                      </Box>
                    </Tooltip>
                  );
                })}
              </VStack>
            )}
          </Box>
        ))}
      </VStack>
    </Box>
  );
};
